/**
 * Utility functions for consistent formatting across the application
 */

/**
 * Formats a phone number to (XXX) XXX-XXXX format
 * @param phoneNumber - The phone number to format (can be digits only or already formatted)
 * @returns Formatted phone number as (XXX) XXX-XXXX
 */
export const formatPhoneNumber = (phoneNumber: string): string => {
  if (!phoneNumber) return '';
  
  // Remove all non-digit characters
  const digitsOnly = phoneNumber.replace(/\D/g, '');
  
  // Format the phone number based on length
  if (digitsOnly.length <= 3) {
    return digitsOnly.length ? `(${digitsOnly}` : '';
  } else if (digitsOnly.length <= 6) {
    return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3)}`;
  } else {
    return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6, 10)}`;
  }
};

/**
 * Formats a time string to 12-hour format with AM/PM
 * @param timeStr - Time string in 24-hour format (HH:MM) or 12-hour format, or time slot range (HH:MM-HH:MM)
 * @returns Formatted time as h:mm AM/PM or h:mm AM/PM - h:mm AM/PM for ranges
 */
export const formatTime12Hour = (timeStr: string): string => {
  if (!timeStr || timeStr === 'closed') return 'Closed';
  
  try {
    // Check if it's a time slot range (HH:MM-HH:MM)
    if (timeStr.includes('-')) {
      const [start, end] = timeStr.split('-');
      return `${formatTime12Hour(start)} - ${formatTime12Hour(end)}`;
    }
    
    // Check if the time is already in 12-hour format with AM/PM
    if (timeStr.includes('AM') || timeStr.includes('PM')) {
      return timeStr;
    }
    
    // Parse the time string
    const timeParts = timeStr.split(':');
    if (timeParts.length !== 2) {
      throw new Error('Invalid time format');
    }
    
    const [hours, minutes] = timeParts.map(Number);
    
    // Validate the parsed numbers
    if (isNaN(hours) || isNaN(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
      throw new Error('Invalid time values');
    }
    
    // Determine AM/PM
    const period = hours >= 12 ? 'PM' : 'AM';
    
    // Convert to 12-hour format
    const hour12 = hours % 12 || 12;
    
    // Return formatted time
    return `${hour12}:${minutes.toString().padStart(2, '0')} ${period}`;
  } catch (error) {
    console.error('Error formatting time:', timeStr, error);
    return 'Invalid time';
  }
};

/**
 * Formats a date to a readable string
 * @param date - Date object or date string
 * @param format - Format string (default: 'MMMM d, yyyy')
 * @returns Formatted date string
 */
export const formatDate = (date: Date | string, format: string = 'MMMM d, yyyy'): string => {
  if (!date) return '';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  // Use date-fns format function
  const { format: dateFnsFormat } = require('date-fns');
  return dateFnsFormat(dateObj, format);
}; 